import { Badge, Button, CARRIER_MARKS, CarrierLogo, DataTable, notify, Section, useConfirm, type Column } from "@flexfreight/ui";
import type { CarrierCode } from "@flexfreight/core";
import "@flexfreight/ui/carrier.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, type ChangeEvent } from "react";
import { api, ApiError } from "../../api/client";

export type CarrierLogoEntry = {
  key: string;
  name: string;
  /** Registry carrier code (its bundled official file shows when there is no logoUrl). */
  code: string | null;
  source: "upload" | "eagleload" | null;
  logoUrl: string | null;
  seenOnEagleload: boolean;
  lastSeenAt: string | null;
  uploadedAt: string | null;
  uploadedBy: string | null;
  eagleload: "none" | "ok" | "failed" | "hidden";
  eagleloadError: string | null;
};

const KEY = ["console", "carrier-logos"] as const;
const TYPES = ["image/png", "image/jpeg", "image/webp"];
export const MAX_LOGO_BYTES = 512 * 1024;
const day = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

const bundled = (code: string | null) => (code && code in CARRIER_MARKS ? CARRIER_MARKS[code as CarrierCode].kind === "logo" : false);

function base64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

function sourceText(e: CarrierLogoEntry): string {
  if (e.source === "upload") return `Uploaded${e.uploadedBy ? ` by ${e.uploadedBy}` : ""}${e.uploadedAt ? ` on ${day.format(new Date(e.uploadedAt))}` : ""}`;
  if (e.source === "eagleload") return "Official logo from EagleLoad";
  if (bundled(e.code)) return "Official logo file (built in)";
  if (e.eagleload === "failed") return "EagleLoad's logo couldn't be used; retried hourly";
  if (e.eagleload === "hidden") return "EagleLoad's logo removed";
  return "Text badge on rates";
}

/**
 * Admin Center > Carriers & API keys > Carrier logos: every carrier seen on EagleLoad rates plus the registry carriers,
 * with the logo rates show. Staff with carriers:manage upload the carrier's official PNG, JPEG or WebP file (used
 * as-is, never redrawn) or remove the one shown; an upload wins over EagleLoad's.
 */
export function CarrierLogosSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<{ logos: CarrierLogoEntry[] }>("/v1/console/carrier-logos") });
  const { confirm, dialog } = useConfirm();
  const input = useRef<HTMLInputElement>(null);
  const target = useRef<CarrierLogoEntry | null>(null);

  const upload = useMutation({
    mutationFn: ({ e, contentType, dataBase64 }: { e: CarrierLogoEntry; contentType: string; dataBase64: string }) =>
      api<{ logos: CarrierLogoEntry[] }>(`/v1/console/carrier-logos/${encodeURIComponent(e.key)}`, { method: "PUT", body: { contentType, dataBase64 } }),
    onSuccess: (data, v) => {
      qc.setQueryData(KEY, data);
      notify.success(`${v.e.name} logo saved`, "Rates show it from now on.");
    },
    onError: (err) => notify.error("Logo not saved", message(err)),
  });
  const remove = useMutation({
    mutationFn: (e: CarrierLogoEntry) => api<{ logos: CarrierLogoEntry[] }>(`/v1/console/carrier-logos/${encodeURIComponent(e.key)}`, { method: "DELETE" }),
    onSuccess: (data, e) => {
      qc.setQueryData(KEY, data);
      notify.success(`${e.name} logo removed`);
    },
    onError: (err) => notify.error("Logo not removed", message(err)),
  });

  function pick(e: CarrierLogoEntry) {
    target.current = e;
    input.current?.click();
  }

  async function chosen(ev: ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    const e = target.current;
    if (!file || !e) return;
    if (!TYPES.includes(file.type)) return notify.error("Logo not saved", "Use the carrier's official PNG, JPEG or WebP file. SVG isn't accepted.");
    if (file.size > MAX_LOGO_BYTES) return notify.error("Logo not saved", "The file is larger than 512 KB.");
    upload.mutate({ e, contentType: file.type, dataBase64: base64(await file.arrayBuffer()) });
  }

  async function askRemove(e: CarrierLogoEntry) {
    const body = e.source === "upload" ? "Rates go back to EagleLoad's logo for this carrier, or the text badge if there is none." : "Rates show the text badge for this carrier until a logo is uploaded.";
    if (await confirm({ title: `Remove the ${e.name} logo?`, body, confirmLabel: "Remove", danger: true })) remove.mutate(e);
  }

  const logos = q.data?.logos ?? [];
  const columns: Column<CarrierLogoEntry>[] = [
    {
      key: "logo",
      header: "Logo",
      width: "8rem",
      render: (e) => (e.logoUrl !== null || bundled(e.code) ? <CarrierLogo code={e.code ?? "eagleload"} name={e.name} logoUrl={e.logoUrl} showName={false} size="sm" /> : <span className="ffa-muted">No logo</span>),
    },
    { key: "carrier", header: "Carrier", primary: true, truncate: true, text: (e) => e.name, render: (e) => e.name, sub: (e) => sourceText(e), subText: (e) => (e.eagleloadError ? `${sourceText(e)}: ${e.eagleloadError}` : sourceText(e)) },
    {
      key: "seen",
      header: "Rates",
      width: "15rem",
      mobile: "hide",
      render: (e) => (
        <span className="ffx-cellrow">
          {e.seenOnEagleload ? <Badge tone="info" size="sm" dot={false}>On EagleLoad rates</Badge> : null}
          {e.code ? <Badge size="sm" dot={false}>Direct carrier</Badge> : null}
        </span>
      ),
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: "Actions",
            align: "end" as const,
            width: "12rem",
            hideable: false,
            render: (e: CarrierLogoEntry) => (
              <span className="ffa-rowactions">
                <Button size="sm" loading={upload.isPending && upload.variables?.e.key === e.key} onClick={() => pick(e)}>
                  {e.source === "upload" ? "Replace" : "Upload logo"}
                </Button>
                {e.source ? (
                  <Button size="sm" variant="ghost" loading={remove.isPending && remove.variables?.key === e.key} onClick={() => void askRemove(e)}>
                    Remove
                  </Button>
                ) : null}
              </span>
            ),
          },
        ]
      : []),
  ];
  return (
    <Section title="Carrier logos" description="Shown on rates in the portal and the quoting center. Only the carrier's official file, used as-is; carriers without one keep their text badge." flush>
      <DataTable
        caption="Carrier logos"
        columns={columns}
        rows={logos}
        rowKey={(e) => e.key}
        maxHeight="none"
        loading={q.isPending}
        error={q.isError ? { title: "Couldn't load carrier logos", message: message(q.error), onRetry: () => void q.refetch() } : null}
        empty="No carriers seen yet."
      />
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(ev) => void chosen(ev)} aria-hidden="true" tabIndex={-1} />
      {dialog}
    </Section>
  );
}
